import {
    PropsWithChildren,
    createContext,
    useContext,
    useEffect,
    useState
} from "react";

type OneTrustContextType = {
    analyticsOptOut: boolean;
};

const OneTrustContext = createContext<OneTrustContextType>({
    analyticsOptOut: true
});

export const initializeOneTrustScript = () => {
    const script = document.createElement("script");
    if (process.env["ONETRUST_SCRIPT_URL"]) {
        script.src = process.env["ONETRUST_SCRIPT_URL"];
    }
    if (process.env["ONETRUST_SCRIPT_GUID"]) {
        script.setAttribute("data-domain", process.env["ONETRUST_SCRIPT_GUID"]);
    }
    if (process.env["ONETRUST_DATA_TEST"]) {
        script.setAttribute("data-test", process.env["ONETRUST_DATA_TEST"]);
    }

    script.type = "text/javascript";
    script.setAttribute("data-geofence", "false");
    script.setAttribute("data-sync", "true");
    script.id = "onetrust";

    document.body.appendChild(script);
    return () => {
        document.body.removeChild(script);
    };
};

/**
 * Custom hook to return OneTrustContext.
 * @returns @type {OneTrustContextType}
 */
export const useOneTrustContext = (): OneTrustContextType => {
    const context = useContext(OneTrustContext);
    if (!context) {
        throw new Error(
            "calls to useOneTrustContext must be wrapped in <OneTrustProvider>"
        );
    }

    return context;
};

/**
 * Check `window.OnetrustActiveGroups` every quater of sec to see if OneTrust has been initialized.
 * OneTrust script is referenced from the head HTML tag and sets the `window.OnetrustActiveGroups` and `OptanonConsent` cookie once it has been initialized.
 */
export const OneTrustProvider = ({ children }: PropsWithChildren) => {
    const [analyticsOptOut, setAnalyticsOptOut] = useState(true);

    useEffect(() => {
        const checkOnetrustActiveGroups = () => {
            if (window.OnetrustActiveGroups === undefined) {
                return;
            }

            const currentAnalyticsOptOut =
                !window.OnetrustActiveGroups.includes(",2,");
            if (analyticsOptOut !== currentAnalyticsOptOut) {
                setAnalyticsOptOut(currentAnalyticsOptOut);
            }

            /**
             * Clear the interval once the OneTrust has been initialized and only recheck every 1 sec if the optout status is still the default (true).
             */
            clearInterval(setIntervalId);
            if (currentAnalyticsOptOut) {
                setIntervalId = setInterval(checkOnetrustActiveGroups, 1000);
            }
        };

        let setIntervalId = setInterval(checkOnetrustActiveGroups, 250);
        return () => {
            clearInterval(setIntervalId);
        };
    }, []);

    return (
        <OneTrustContext.Provider value={{ analyticsOptOut }}>
            {children}
        </OneTrustContext.Provider>
    );
};
